import React, { useEffect, useState } from "react";
import axios from "axios";
import toastr from "toastr";
import { useOutletContext, useParams } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AddConfirmationFeed = ({ viewUserId = null }) => {
  const userLogin = JSON.parse(localStorage.getItem("user") || "{}");
  // const { userId } = useParams();  
  const { userId: routeUserId } = useParams();
  const userId = viewUserId !== null && viewUserId !== undefined && viewUserId !== "" ? viewUserId : routeUserId;
  const [user, setUser] = useState(null);
  const [fields, setFields] = useState([]);
  const [feedback, setFeedback] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { token } = useOutletContext();
  const isSameUser = Number(userLogin?.id) === Number(userId);

  // ---------------------------------------------------------
  // FETCH CONFIRMATION DATA
  // ---------------------------------------------------------
  const fetchConfirmationData = async () => {
    if (!userId) return;

    setLoading(true);

    try {
      const res = await axios.get(`${API_BASE_URL}/employeeinfos/confirmation_feedback/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 200) {
        setUser(res.data.user || {});
        setFields(res.data.fields || []);

        // Convert existing DB feedback into easy lookup object
        const existingFeedback = {};

        (res.data.conffeed || []).forEach((item) => {
          existingFeedback[item.confirmation_process_field_id] = {
            rating: item.rating ?? "",
            comment: item.comment ?? "",
          };
        });

        setFeedback(existingFeedback);
      }
    } catch (error) {
      console.error("Failed to fetch confirmation data:", error);

      toastr.error(
        error?.response?.data?.message ||
          "Failed to load confirmation feedback"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfirmationData();
  }, [userId]);

  // ---------------------------------------------------------
  // UPDATE RATING
  // ---------------------------------------------------------
  const handleRatingChange = (fieldId, value) => {
    setFeedback((prev) => ({
      ...prev,
      [fieldId]: {
        ...(prev[fieldId] || {}),
        rating: value,
      },
    }));
  };

  // ---------------------------------------------------------
  // UPDATE COMMENT
  // ---------------------------------------------------------
  const handleCommentChange = (fieldId, value) => {
    setFeedback((prev) => ({
      ...prev,
      [fieldId]: {
        ...(prev[fieldId] || {}),
        comment: value,
      },
    }));
  };

  // ---------------------------------------------------------
  // SUBMIT
  // ---------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fields.length) {
      toastr.error("No confirmation fields found");
      return;
    }

    setSaving(true);

    try {
      const confirmation_process_field_id = [];
      const rating = [];
      const comment = [];

      fields.forEach((field) => {
        confirmation_process_field_id.push(field.id);

        rating.push(
          feedback[field.id]?.rating !== undefined
            ? feedback[field.id].rating
            : null
        );

        comment.push(feedback[field.id]?.comment || "");
      });

      const payload = {
        confirmation_process_field_id,
        rating,
        comment,
      };

      const res = await axios.post(
        `${API_BASE_URL}/employeeinfos/confirmation_feedback/${userId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.status === 200) {
        toastr.success(
          res.data.message || "Confirmation feedback saved successfully"
        );

        // Reload to make sure UI contains latest data
        await fetchConfirmationData();
      }
    } catch (error) {
      console.error("Save confirmation feedback error:", error);

      toastr.error(
        error?.response?.data?.message ||
          "Failed to save confirmation feedback"
      );
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------
  // LOADING
  // ---------------------------------------------------------
  if (loading) {
    return (
      <div
        style={{
          padding: 30,
          textAlign: "center",
          fontSize: 15,
        }}
      >
        Loading confirmation feedback...
      </div>
    );
  }

  // ---------------------------------------------------------
  // FIELD HELPERS
  // ---------------------------------------------------------
  const parentFields = fields.filter(
    (field) => Number(field.parent) === 0
  );

  const getChildFields = (parentId) => {
    return fields.filter(
      (field) => Number(field.parent) === Number(parentId)
    );
  };

  // Same logic as your PHP:
  // 4,5,9,10 are comment-only fields
  const commentOnlyFields = [4, 5, 9, 10];

  return (
    <div
      style={{
        padding: 20,
        width: "100%",
      }}
    >
      <h3 style={{ marginBottom: 20 }}>
        Confirmation Feedback Form For{" "}
        {user?.firstname} {user?.lastname}
      </h3>

      <form onSubmit={handleSubmit}>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 800,
            }}
          >
            <thead>
              <tr
                style={{
                  background: "#53534b",
                  color: "#fff",
                }}
              >
                <th
                  style={{
                    width: "45%",
                    border: "1px solid #ddd",
                    padding: 10,
                    textAlign: "left",
                  }}
                >
                  Between Week 11-12 For purpose of Confirmation
                </th>

                <th
                  style={{
                    width: "10%",
                    border: "1px solid #ddd",
                    padding: 10,
                  }}
                >
                  Rating
                  <span
                    title="1 - Doesn't Meet Expectations, 3 - Meets Expectations, 5 - Exceeds Expectations"
                    style={{
                      marginLeft: 5,
                      cursor: "help",
                    }}
                  >
                    ⓘ
                  </span>
                </th>

                <th
                  style={{
                    width: "45%",
                    border: "1px solid #ddd",
                    padding: 10,
                    textAlign: "left",
                  }}
                >
                  Comments
                </th>
              </tr>
            </thead>

            <tbody>
              {parentFields.map((parent) => {
                const children = getChildFields(parent.id);

                // =====================================================
                // COMMENT ONLY QUESTIONS
                // Same as PHP $userFields = array(4,5,9,10)
                // =====================================================
                if (commentOnlyFields.includes(Number(parent.id))) {
                  return (
                    <tr key={parent.id}>
                      <td
                        style={{
                          border: "1px solid #ddd",
                          padding: 10,
                          fontWeight: 600,
                        }}
                      >
                        {parent.description}
                      </td>

                      <td
                        colSpan={2}
                        style={{
                          border: "1px solid #ddd",
                          padding: 10,
                        }}
                      >
                        <textarea
                          value={feedback[parent.id]?.comment || ""}
                          onChange={(e) =>
                            handleCommentChange(
                              parent.id,
                              e.target.value
                            )
                          }
                          placeholder="Enter comments..."
                          rows={3}
                          style={{
                            width: "100%",
                            minHeight: 70,
                            padding: 8,
                            border: "1px solid #ddd",
                            borderRadius: 4,
                            resize: "vertical",
                            boxSizing: "border-box",
                          }}
                        />
                      </td>
                    </tr>
                  );
                }

                // =====================================================
                // NORMAL PARENT SECTION
                // =====================================================
                return (
                  <React.Fragment key={parent.id}>
                    <tr>
                      <td
                        colSpan={3}
                        style={{
                          background: "#808080",
                          color: "#fff",
                          border: "1px solid #ddd",
                          padding: 10,
                        }}
                      >
                        <strong>{parent.description}</strong>
                      </td>
                    </tr>

                    {children.map((field) => (
                      <tr key={field.id}>
                        <td
                          style={{
                            border: "1px solid #ddd",
                            padding: 10,
                            verticalAlign: "top",
                          }}
                        >
                          {field.description}
                        </td>

                        <td
                          style={{
                            border: "1px solid #ddd",
                            padding: 10,
                            verticalAlign: "top",
                            textAlign: "center",
                          }}
                        >
                          <select
                            value={feedback[field.id]?.rating || ""}
                            onChange={(e) =>
                              handleRatingChange(
                                field.id,
                                e.target.value
                              )
                            }
                            style={{
                              width: "70px",
                              padding: "6px",
                              textAlign: "center",
                              border: "1px solid #ccc",
                              borderRadius: 4,
                            }}
                          >
                            <option value="">-</option>
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5">5</option>
                          </select>
                        </td>

                        <td
                          style={{
                            border: "1px solid #ddd",
                            padding: 10,
                          }}
                        >
                          <textarea
                            value={feedback[field.id]?.comment || ""}
                            onChange={(e) =>
                              handleCommentChange(
                                field.id,
                                e.target.value
                              )
                            }
                            placeholder="Enter comments..."
                            rows={3}
                            style={{
                              width: "100%",
                              minHeight: 60,
                              padding: 8,
                              border: "1px solid #ddd",
                              borderRadius: 4,
                              resize: "vertical",
                              boxSizing: "border-box",
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* =====================================================
            SUBMIT BUTTON
        ===================================================== */}
        {!isSameUser && (
          <div
            style={{
              marginTop: 20,
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
            }}
          >
            <button
              type="button"
              onClick={fetchConfirmationData}
              disabled={saving}
              style={{
                padding: "9px 20px",
                border: "1px solid #ccc",
                borderRadius: 4,
                background: "#fff",
                cursor: "pointer",
              }}
            >
              Reset
            </button>

            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "9px 25px",
                border: "none",
                borderRadius: 4,
                background: "#53534b",
                color: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: 600,
              }}
            >
              {saving ? "Saving..." : "Submit Feedback"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default AddConfirmationFeed;